import axios from 'axios';
const {
    CancelToken
} = axios;

const {
    REACT_APP_API_URL,
    REACT_APP_REQUEST_TIMEOUT_SECONDS
} = process.env;

/**
 * Public Read API: Rawmotion's Live Scoreboard Interface
 * @class Api
 */
class Api {
    constructor() {
        this.waiting = {};
    }

    serializeQueryParams(params) {
        return Object
            .keys(params || {})
            .map((key) => {
                return `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`;
            })
            .join('&');
    }

    mergeQueryParams(parameters, queryParameters) {
        if (parameters.$queryParameters) {
            Object.keys(parameters.$queryParameters)
                .forEach(function(parameterName) {
                    const parameter = parameters.$queryParameters[parameterName];
                    queryParameters[parameterName] = parameter;
                });
        }

        return queryParameters;
    }

    /**
     * HTTP Request
     * @method
     * @name Api#request
     * @param {string} method - http method
     * @param {string} url - url to do request
     * @param {object} parameters
     * @param {object} data - body parameters / object
     * @param {object} headers - header parameters
     * @param {object} queryParams - querystring parameters
     * @param {object} form - form data object
     */
    request(method, url, parameters, data, headers, queryParams, form) {
        const query = queryParams && Object.keys(queryParams).length ?
            this.serializeQueryParams(queryParams) :
            null;

        const urlWithParams = url + (query ? '?' + query : '');

        if (data && !Object.keys(data).length) {
            data = undefined;
        }

        if (method === 'GET') {
            return this.get({
                url: urlWithParams,
                method,
                headers,
                data: JSON.stringify(data)
            });
        }

        return axios({
                url: urlWithParams,
                method,
                headers,
                data
            })
            .then(res => res.data);
    };

    get({
        url,
        method,
        headers,
        data
    }) {
        const prevGet = this.waiting[url];

        if (prevGet) {
            if (!prevGet.done && prevGet.timeoutDate > new Date()) {
                return prevGet.request;
            }

            if (!prevGet.done) {
                prevGet.abort();
            }

            this.waiting[url] = null;
        }

        const cancelSource = CancelToken.source();

        this.waiting[url] = {
            request: axios({
                    url,
                    method,
                    headers,
                    data,
                    cancelToken: cancelSource.token
                })
                .then((res) => {
                    if (this.waiting[url]) {
                        this.waiting[url].done = true;
                    }

                    return res.data;
                })
                .catch((err) => {
                    if (this.waiting[url]) {
                        this.waiting[url].done = true;
                    }

                    throw err;
                }),
            abort: () => cancelSource.cancel(),
            timeoutDate: new Date(
                Date.now() +
                REACT_APP_REQUEST_TIMEOUT_SECONDS * 1000
            )
        };

        return this.waiting[url].request;
    }

    /**
     * Get an event
     * @method
     * @name Api#eventShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - Event ID, 3rd Party ID (with a prefix of 3rd_) or the short name of the event.
     */
    eventShow(parameters = {}) {
        let path = '/event/{eventId}';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get all events
     * @method
     * @name Api#eventsShow
     * @param {object} parameters - method options and parameters
     * @param {array} parameters.sports - Public Read API: Rawmotion's Live Scoreboard Interface
     * @param {number} parameters.year - Public Read API: Rawmotion's Live Scoreboard Interface
     * @param {string} parameters.country - Public Read API: Rawmotion's Live Scoreboard Interface
     */
    eventsShow(parameters = {}) {
        let path = '/events/';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        if (parameters['sports'] !== undefined) {
            queryParameters['sports'] = parameters['sports'];
        }

        if (parameters['year'] !== undefined) {
            queryParameters['year'] = parameters['year'];
        }

        if (parameters['country'] !== undefined) {
            queryParameters['country'] = parameters['country'];
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get a contest
     * @method
     * @name Api#contestShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.contestId - External ID or 3rd Party ID with a prefix of 3rd_
     */
    contestShow(parameters = {}) {
        let path = '/event/{eventId}/contest/{contestId}';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        path = path.replace('{contestId}', parameters['contestId']);

        if (parameters['contestId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: contestId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get all contests for an event
     * @method
     * @name Api#contestsShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     */
    contestsShow(parameters = {}) {
        let path = '/event/{eventId}/contests/';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get a specific rounds results
     * @method
     * @name Api#roundShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.contestId - External ID or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.roundId - External ID or 3rd Party ID with a prefix of 3rd_
     */
    roundShow(parameters = {}) {
        let path = '/event/{eventId}/contest/{contestId}/round/{roundId}';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        path = path.replace('{contestId}', parameters['contestId']);

        if (parameters['contestId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: contestId')
            ));
        }

        path = path.replace('{roundId}', parameters['roundId']);

        if (parameters['roundId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: roundId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get all rounds for a contest
     * @method
     * @name Api#roundsShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.contestId - External ID or 3rd Party ID with a prefix of 3rd_
     */
    roundsShow(parameters = {}) {
        let path = '/event/{eventId}/contest/{contestId}/rounds';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        path = path.replace('{contestId}', parameters['contestId']);

        if (parameters['contestId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: contestId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * List documents for a round
     * @method
     * @name Api#documentsList
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.contestId - External ID or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.roundId - External ID or 3rd Party ID with a prefix of 3rd_
     */
    documentsList(parameters = {}) {
        let path = '/event/{eventId}/contest/{contestId}/round/{roundId}/document';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        path = path.replace('{contestId}', parameters['contestId']);

        if (parameters['contestId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: contestId')
            ));
        }

        path = path.replace('{roundId}', parameters['roundId']);

        if (parameters['roundId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: roundId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
    /**
     * Get a specific heat
     * @method
     * @name Api#heatShow
     * @param {object} parameters - method options and parameters
     * @param {string} parameters.eventId - ID of the event or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.contestId - External ID or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.roundId - External ID or 3rd Party ID with a prefix of 3rd_
     * @param {string} parameters.heatId - External ID or 3rd Party ID with a prefix of 3rd_
     */
    heatShow(parameters = {}) {
        let path = '/event/{eventId}/contest/{contestId}/round/{roundId}/heat/{heatId}';
        let body = {};
        let queryParameters = {};
        let form = {};

        const headers = {
            'Accept': 'application/json',
        };

        path = path.replace('{eventId}', parameters['eventId']);

        if (parameters['eventId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: eventId')
            ));
        }

        path = path.replace('{contestId}', parameters['contestId']);

        if (parameters['contestId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: contestId')
            ));
        }

        path = path.replace('{roundId}', parameters['roundId']);

        if (parameters['roundId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: roundId')
            ));
        }

        path = path.replace('{heatId}', parameters['heatId']);

        if (parameters['heatId'] === undefined) {
            return new Promise((resolve, reject) => reject(
                Error('Missing required  parameter: heatId')
            ));
        }

        return this.request(
            'GET',
            `${REACT_APP_API_URL}${path}`,
            parameters, body,
            headers,
            this.mergeQueryParams(
                parameters,
                queryParameters
            ),
            form
        );
    };
};

const instance = new Api();
export default instance;